import {
  interpolateRoute,
  API_ROUTES,
  PaginatedResponse,
  ApiPaginatedResponse,
  type SenderType,
  type ThreadEventDataMap,
  type ThreadEventType,
} from '@super/shared-types'
import { BaseApiClient } from './base-client.js'

export interface ThreadWithDetails {
  id: string
  contactId: string
  companyId: string
  threadType: 'PHONE' | 'EMAIL'
  phone?: string
  email?: string
  createdAt: string
  updatedAt: string
  contact: {
    id: string
    name: string
    senderType?: SenderType
    email?: string
    phone?: string
    avatar?: string
  }
  threadEvents?: Array<{
    id: string
    eventType: ThreadEventType
    eventTime: string
    eventData: ThreadEventDataMap[ThreadEventType]
  }>
  _count?: {
    messages: number
  }
}

export type ThreadsResponse = PaginatedResponse<ThreadWithDetails>

export interface GetThreadsParams {
  threadType?: 'PHONE' | 'EMAIL'
  limit?: number
  cursor?: string
  direction?: 'before' | 'after'
  includeDetails?: boolean
}

export interface AdminGetThreadsParams extends GetThreadsParams {
  companyId: string
}

class ThreadsApiClient extends BaseApiClient {
  async getThreads(params: GetThreadsParams = {}): Promise<ThreadsResponse> {
    const queryString = this.buildQueryString(params)
    const response = await this.request<
      ApiPaginatedResponse<ThreadWithDetails>
    >(`${API_ROUTES.THREADS.GET_ALL}${queryString}`)
    return { data: response.data, pagination: response.pagination }
  }

  async getThreadsByContact(
    contactId: string,
    params: Omit<GetThreadsParams, 'contactId'> = {},
  ): Promise<ThreadsResponse> {
    const queryString = this.buildQueryString(params)
    const path = interpolateRoute(API_ROUTES.THREADS.GET_BY_CONTACT, {
      contactId,
    })
    const response = await this.request<
      ApiPaginatedResponse<ThreadWithDetails>
    >(`${path}${queryString}`)
    return { data: response.data, pagination: response.pagination }
  }

  async getThreadById(id: string): Promise<ThreadWithDetails> {
    const path = interpolateRoute(API_ROUTES.THREADS.GET_BY_ID, { id })
    const response = await this.request<{ data: ThreadWithDetails }>(path)
    return response.data
  }

  async getThreadsAroundThread(
    threadId: string,
    params: Omit<GetThreadsParams, 'cursor' | 'direction'> = {},
  ): Promise<ThreadsResponse> {
    const queryString = this.buildQueryString(params)
    const path = interpolateRoute(API_ROUTES.THREADS.GET_AROUND_THREAD, {
      threadId,
    })
    const response = await this.request<
      ApiPaginatedResponse<ThreadWithDetails>
    >(`${path}${queryString}`)
    return { data: response.data, pagination: response.pagination }
  }
}

// Admin threads client for super data admins
class AdminThreadsApiClient extends BaseApiClient {
  async getThreads(params: AdminGetThreadsParams): Promise<ThreadsResponse> {
    const queryString = this.buildQueryString(params)
    const response = await this.request<
      ApiPaginatedResponse<ThreadWithDetails>
    >(`${API_ROUTES.ADMIN.THREADS.GET_ALL}${queryString}`)
    return { data: response.data, pagination: response.pagination }
  }

  async getThreadsByContact(
    contactId: string,
    params: Omit<AdminGetThreadsParams, 'contactId'>,
  ): Promise<ThreadsResponse> {
    const queryString = this.buildQueryString(params)
    const path = interpolateRoute(API_ROUTES.ADMIN.THREADS.GET_BY_CONTACT, {
      contactId,
    })
    const response = await this.request<
      ApiPaginatedResponse<ThreadWithDetails>
    >(`${path}${queryString}`)
    return { data: response.data, pagination: response.pagination }
  }

  async getThreadById(id: string): Promise<ThreadWithDetails> {
    const path = interpolateRoute(API_ROUTES.ADMIN.THREADS.GET_BY_ID, { id })
    const response = await this.request<{ data: ThreadWithDetails }>(path)
    return response.data
  }

  async getThreadsAroundThread(
    threadId: string,
    params: Omit<AdminGetThreadsParams, 'cursor' | 'direction'>,
  ): Promise<ThreadsResponse> {
    const queryString = this.buildQueryString(params)
    const path = interpolateRoute(API_ROUTES.ADMIN.THREADS.GET_AROUND_THREAD, {
      threadId,
    })
    const response = await this.request<
      ApiPaginatedResponse<ThreadWithDetails>
    >(`${path}${queryString}`)
    return { data: response.data, pagination: response.pagination }
  }
}

export const threadsClient = new ThreadsApiClient()
export const adminThreadsClient = new AdminThreadsApiClient()

/**
 * Helper function to get the appropriate threads client based on user role
 * Use this in React components where you have access to the auth context
 */
export function getThreadsClient(isSuperAdmin: true): AdminThreadsApiClient
export function getThreadsClient(isSuperAdmin: false): ThreadsApiClient
export function getThreadsClient(isSuperAdmin: boolean) {
  return isSuperAdmin ? adminThreadsClient : threadsClient
}
